// src/pages/CustomerPriceSetting.js
import React, { useState, useEffect } from 'react';
import { MdEdit, MdDelete } from 'react-icons/md';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import BASE_URL from '../../api';

const API_BASE_URL = `${BASE_URL}/api/master`;

const CustomerPriceSetting = () => {
  const [records, setRecords] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [states, setStates] = useState([]);
  const [fromAreas, setFromAreas] = useState([]);
  const [toAreas, setToAreas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [showList, setShowList] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [editingId, setEditingId] = useState(null);
  const [fromSearch, setFromSearch] = useState('');
  const [toSearch, setToSearch] = useState('');
  const pageSize = 10;

  // Form State
  const [form, setForm] = useState({
    customerId: '',
    fromStateId: '',
    toStateId: '',
    fromLocationId: [],
    toLocationId: [],
    isMetro: 0,
    modeId: 1,
    isOda: 0,
    ratePerKg: '',
    adRate: '',
    pickupOda: '',
    deliveryOda: '',
    minAmount: '',
    minKgs: '',
    maxKgs: '',
    minChrgWeight: '',
    codChrg: '',
    iata: '',
    cft: '',
    docChrg: '',
    fovPercent: '',
    hamaliChrg: '',
    fuelChrg: '',
    loadingCharge: '',
    unloadingCharge: '',
    otherCharge: '',
    coCourier: '',
    fod: '',
    adjWeight: '',
    holidayCharges: '',
    specialCharges: '',
    flatAmount: '',
    pickupCharge: '',
    deliveryCharge: '',
    freightAmount: '',
    finalBreakup: '',
    volume: '',
    priceBox: '',
    status: true,
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  // ==================== FETCH DATA ====================
  const fetchCustomers = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/customer`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch customers');
      const { data } = await res.json();
      setCustomers(Array.isArray(data) ? data.map(c => ({ _id: c._id, name: c.name || c.companyName })) : []);
    } catch (err) {
      console.error(err);
    }
  };



  const fetchStates = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/state`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch states');
      const { data } = await res.json();
      setStates(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchAreas = async (stateId) => {
    if (!stateId) return [];
    try {
      const res = await fetch(`${API_BASE_URL}/area?stateid=${stateId}`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch areas');
      const { data } = await res.json();

      // Updated Mapping to include pincode
      return Array.isArray(data) ? data.map(area => ({
        ...area,
        displayName: `${area.areaname} - ${area.pincode?.pincode || 'N/A'}`
      })) : [];
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/customerpricesetting`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch records');
      const { data } = await res.json();
      setRecords(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.message || 'Error fetching records');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      await Promise.all([fetchCustomers(), fetchStates(), fetchRecords()]);
    };
    if (localStorage.getItem('token')) load();
  }, []);

  // Load areas when state changes
  useEffect(() => {
    if (form.fromStateId) {
      fetchAreas(form.fromStateId).then(setFromAreas);
    } else {
      setFromAreas([]);
      setForm(prev => ({ ...prev, fromLocationId: [] }));
    }
  }, [form.fromStateId]);

  useEffect(() => {
    if (form.toStateId) {
      fetchAreas(form.toStateId).then(setToAreas);
    } else {
      setToAreas([]);
      setForm(prev => ({ ...prev, toLocationId: [] }));
    }
  }, [form.toStateId]);


  // 1. Filtered Lists
  const filteredFromAreas = fromAreas.filter(area =>
    area.displayName.toLowerCase().includes(fromSearch.toLowerCase())
  );

  const filteredToAreas = toAreas.filter(area =>
    area.displayName.toLowerCase().includes(toSearch.toLowerCase())
  );

  // 2. Toggle All Handler
  const handleSelectAll = (type, filteredItems) => {
    const fieldName = type === 'from' ? 'fromLocationId' : 'toLocationId';
    const currentIds = form[fieldName];
    const filteredIds = filteredItems.map(item => item._id);

    // Check if all filtered items are already selected
    const allSelected = filteredIds.every(id => currentIds.includes(id));

    if (allSelected) {
      // Unselect only the filtered items
      setForm(prev => ({
        ...prev,
        [fieldName]: prev[fieldName].filter(id => !filteredIds.includes(id))
      }));
    } else {
      // Select all filtered items (merge with existing selections)
      setForm(prev => ({
        ...prev,
        [fieldName]: [...new Set([...prev[fieldName], ...filteredIds])]
      }));
    }
  };

  // ==================== CRUD ====================
  const createRecord = async (data) => {
    const res = await fetch(`${API_BASE_URL}/customerpricesetting`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to create');
    }
    return res.json();
  };

  const updateRecord = async (id, data) => {
    const res = await fetch(`${API_BASE_URL}/customerpricesetting/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to update');
    }
    return res.json();
  };

  const deleteRecord = async (id) => {
    const res = await fetch(`${API_BASE_URL}/customerpricesetting/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to delete');
    }
    return res.json();
  };

  // ==================== FORM HANDLERS ====================
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setForm(prev => {
        const arr = prev[name] || [];
        if (checked) {
          return { ...prev, [name]: [...arr, value] };
        } else {
          return { ...prev, [name]: arr.filter(v => v !== value) };
        }
      });
    } else if (type === 'number') {
      setForm(prev => ({ ...prev, [name]: parseFloat(value) || 0 }));
    } else {
      setForm(prev => ({ ...prev, [name]: value }));
    }
  };

  const resetForm = () => {
    setForm({
      customerId: '',
      fromStateId: '',
      toStateId: '',
      fromLocationId: [],
      toLocationId: [],
      isMetro: 0,
      modeId: 1,
      isOda: 0,
      ratePerKg: '',
      adRate: '',
      pickupOda: '',
      deliveryOda: '',
      minAmount: '',
      minKgs: '',
      maxKgs: '',
      minChrgWeight: '',
      codChrg: '',
      iata: '',
      cft: '',
      docChrg: '',
      fovPercent: '',
      hamaliChrg: '',
      fuelChrg: '',
      loadingCharge: '',
      unloadingCharge: '',
      otherCharge: '',
      coCourier: '',
      fod: '',
      adjWeight: '',
      holidayCharges: '',
      specialCharges: '',
      flatAmount: '',
      pickupCharge: '',
      deliveryCharge: '',
      freightAmount: '',
      finalBreakup: '',
      volume: '',
      priceBox: '',
      status: true,
    });
    setEditingId(null);
    setFromAreas([]);
    setToAreas([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.customerId || !form.fromStateId || !form.toStateId ||
      form.fromLocationId.length === 0 || form.toLocationId.length === 0) {
      toast.error('Please fill all required fields: Customer, States, and at least one Location each');
      return;
    }

    try {
      setLoading(true);
      if (editingId) {
        await updateRecord(editingId, form);
        toast.success('Updated successfully!');
      } else {
        await createRecord(form);
        toast.success('Created successfully!');
      }
      resetForm();
      setShowList(true);
      fetchRecords();
    } catch (err) {
      toast.error(err.message || 'Error saving record');
    } finally {
      setLoading(false);
    }
  };

  // FIXED EDIT FUNCTION — Now works 100%
  const handleEdit = async (record) => {
    // Extract IDs safely
    const fromStateId = record.fromStateId?._id || record.fromStateId || '';
    const toStateId = record.toStateId?._id || record.toStateId || '';

    // Load areas first
    const [loadedFromAreas, loadedToAreas] = await Promise.all([
      fetchAreas(fromStateId),
      fetchAreas(toStateId)
    ]);

    setFromAreas(loadedFromAreas);
    setToAreas(loadedToAreas);

    // Populate form
    setForm({
      customerId: record.customerId?._id || record.customerId || '',
      fromStateId,
      toStateId,
      fromLocationId: Array.isArray(record.fromLocationId)
        ?._id
        ? record.fromLocationId.map(loc => loc._id)
        : (record.fromLocationId || []),
      toLocationId: Array.isArray(record.toLocationId?._id)
        ? record.toLocationId.map(loc => loc._id)
        : (record.toLocationId || []),
      isMetro: record.isMetro || 0,
      modeId: record.modeId || 1,
      isOda: record.isOda || 0,
      ratePerKg: record.ratePerKg || '',
      adRate: record.adRate || '',
      pickupOda: record.pickupOda || '',
      deliveryOda: record.deliveryOda || '',
      minAmount: record.minAmount || '',
      minKgs: record.minKgs || '',
      maxKgs: record.maxKgs || '',
      minChrgWeight: record.minChrgWeight || '',
      codChrg: record.codChrg || '',
      iata: record.iata || '',
      cft: record.cft || '',
      docChrg: record.docChrg || '',
      fovPercent: record.fovPercent || '',
      hamaliChrg: record.hamaliChrg || '',
      fuelChrg: record.fuelChrg || '',
      loadingCharge: record.loadingCharge || '',
      unloadingCharge: record.unloadingCharge || '',
      otherCharge: record.otherCharge || '',
      coCourier: record.coCourier || '',
      fod: record.fod || '',
      adjWeight: record.adjWeight || '',
      holidayCharges: record.holidayCharges || '',
      specialCharges: record.specialCharges || '',
      flatAmount: record.flatAmount || '',
      pickupCharge: record.pickupCharge || '',
      deliveryCharge: record.deliveryCharge || '',
      freightAmount: record.freightAmount || '',
      finalBreakup: record.finalBreakup || '',
      volume: record.volume || '',
      priceBox: record.priceBox || '',
      status: record.status !== false,
    });

    setEditingId(record._id);
    setShowList(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this pricing record permanently?')) return;
    try {
      setLoading(true);
      await deleteRecord(id);
      toast.success('Deleted successfully');
      fetchRecords();
    } catch (err) {
      toast.error(err.message || 'Error deleting record');
    } finally {
      setLoading(false);
    }
  };

  // ==================== FILTER & PAGINATION ====================
  const filtered = records.filter(r => {
    const custName = customers.find(c => c._id === (r.customerId?._id || r.customerId))?.name || '';
    const fromState = states.find(s => s._id === (r.fromStateId?._id || r.fromStateId))?.statename || '';
    const toState = states.find(s => s._id === (r.toStateId?._id || r.toStateId))?.statename || '';

    const searchTerm = filter.toLowerCase();
    return custName.toLowerCase().includes(searchTerm) ||
      fromState.toLowerCase().includes(searchTerm) ||
      toState.toLowerCase().includes(searchTerm);
  });

  const totalPages = Math.ceil(filtered.length / pageSize);
  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div className="flex">
      <ToastContainer />
      <div className="flex-1 p-6 bg-gray-100 min-h-screen">
        <h2 className="text-3xl font-bold mb-6 text-gray-800">Customer Rate Master</h2>

        <div className="bg-white rounded-xl shadow-lg p-6">
          {/* Toolbar */}
          <div className="flex flex-col sm:flex-row justify-between gap-4 mb-6">
            <input
              type="text"
              placeholder="Search by Customer, From State, To State..."
              value={filter}
              onChange={e => { setFilter(e.target.value); setCurrentPage(1); }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none w-full sm:w-80"
            />
            <button
              onClick={() => {
                setShowList(!showList);
                if (showList) resetForm();
              }}
              className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition"
            >
              {showList ? 'Add New Rate' : 'View List'}
            </button>
          </div>

          {/* FORM */}
          {!showList && (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {/* Required Fields */}
                <div>
                  <label className="block font-semibold text-gray-700">Customer *</label>
                  <select name="customerId" value={form.customerId} onChange={handleChange} required className="w-full mt-1 px-3 py-2 border rounded-lg">
                    <option value="">Select Customer</option>
                    {customers.map(c => (
                      <option key={c._id} value={c._id}>{c.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block font-semibold text-gray-700">From State *</label>
                  <select name="fromStateId" value={form.fromStateId} onChange={handleChange} required className="w-full mt-1 px-3 py-2 border rounded-lg">
                    <option value="">Select State</option>
                    {states.map(s => (
                      <option key={s._id} value={s._id}>{s.statename}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block font-semibold text-gray-700">To State *</label>
                  <select name="toStateId" value={form.toStateId} onChange={handleChange} required className="w-full mt-1 px-3 py-2 border rounded-lg">
                    <option value="">Select State</option>
                    {states.map(s => (
                      <option key={s._id} value={s._id}>{s.statename}</option>
                    ))}
                  </select>
                </div>

                {/* From Locations */}
                {/* From Locations Section */}
                <div className="md:col-span-3">
                  <div className="flex justify-between items-end mb-2">
                    <label className="block font-semibold text-gray-700">From Locations *</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Search area..."
                        className="px-2 py-1 text-sm border rounded"
                        value={fromSearch}
                        onChange={(e) => setFromSearch(e.target.value)}
                      />
                      <button
                        type="button"
                        onClick={() => handleSelectAll('from', filteredFromAreas)}
                        className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 font-medium"
                      >
                        {filteredFromAreas.every(id => form.fromLocationId.includes(id._id)) ? 'Unselect All' : 'Select All'}
                      </button>
                    </div>
                  </div>
                  <div className="border rounded-lg p-4 max-h-48 overflow-y-auto bg-gray-50">
                    {fromAreas.length === 0 ? (
                      <p className="text-gray-500 italic">Please select From State first</p>
                    ) : filteredFromAreas.length === 0 ? (
                      <p className="text-gray-500 italic">No areas match your search</p>
                    ) : (
                      filteredFromAreas.map(area => (
                        <label key={area._id} className="flex items-center py-1 cursor-pointer hover:bg-gray-100 px-2 rounded">
                          <input
                            type="checkbox"
                            name="fromLocationId"
                            value={area._id}
                            checked={form.fromLocationId.includes(area._id)}
                            onChange={handleChange}
                            className="mr-3 h-4 w-4 text-blue-600 rounded"
                          />
                          <span className="text-sm">{area.displayName}</span>
                        </label>
                      ))
                    )}
                  </div>
                </div>

                {/* To Locations Section */}
                <div className="md:col-span-3">
                  <div className="flex justify-between items-end mb-2">
                    <label className="block font-semibold text-gray-700">To Locations *</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Search area..."
                        className="px-2 py-1 text-sm border rounded"
                        value={toSearch}
                        onChange={(e) => setToSearch(e.target.value)}
                      />
                      <button
                        type="button"
                        onClick={() => handleSelectAll('to', filteredToAreas)}
                        className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 font-medium"
                      >
                        {filteredToAreas.every(id => form.toLocationId.includes(id._id)) ? 'Unselect All' : 'Select All'}
                      </button>
                    </div>
                  </div>
                  <div className="border rounded-lg p-4 max-h-48 overflow-y-auto bg-gray-50">
                    {toAreas.length === 0 ? (
                      <p className="text-gray-500 italic">Please select To State first</p>
                    ) : filteredToAreas.length === 0 ? (
                      <p className="text-gray-500 italic">No areas match your search</p>
                    ) : (
                      filteredToAreas.map(area => (
                        <label key={area._id} className="flex items-center py-1 cursor-pointer hover:bg-gray-100 px-2 rounded">
                          <input
                            type="checkbox"
                            name="toLocationId"
                            value={area._id}
                            checked={form.toLocationId.includes(area._id)}
                            onChange={handleChange}
                            className="mr-3 h-4 w-4 text-blue-600 rounded"
                          />
                          <span className="text-sm">{area.displayName}</span>
                        </label>
                      ))
                    )}
                  </div>
                </div>

                {/* Other Fields */}
                <div><label className="block font-medium">Metro</label>
                  <select name="isMetro" value={form.isMetro} onChange={handleChange} className="w-full mt-1 px-3 py-2 border rounded-lg">
                    <option value={0}>No</option><option value={1}>Yes</option>
                  </select>
                </div>
                <div><label className="block font-medium">Mode</label>
                  <select name="modeId" value={form.modeId} onChange={handleChange} className="w-full mt-1 px-3 py-2 border rounded-lg">
                    {['Surface', 'Air', 'Rail', 'Water', 'Multimodal'].map((m, i) =>
                      <option key={i + 1} value={i + 1}>{m}</option>
                    )}
                  </select>
                </div>
                <div><label className="block font-medium">ODA</label>
                  <select name="isOda" value={form.isOda} onChange={handleChange} className="w-full mt-1 px-3 py-2 border rounded-lg">
                    <option value={0}>No</option><option value={1}>Yes</option>
                  </select>
                </div>

                {[
                  'ratePerKg', 'adRate', 'pickupOda', 'deliveryOda', 'minAmount', 'minKgs', 'maxKgs', 'minChrgWeight',
                  'codChrg', 'iata', 'cft', 'docChrg', 'fovPercent', 'hamaliChrg', 'fuelChrg', 'loadingCharge', 'unloadingCharge',
                  'otherCharge', 'coCourier', 'fod', 'flatAmount', 'pickupCharge', 'deliveryCharge',
                  'freightAmount', 'finalBreakup', 'volume', 'priceBox'
                ].map(field => (
                  <div key={field}>
                    <label className="block font-medium text-gray-700">
                      {field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      name={field}
                      value={form[field]}
                      onChange={handleChange}
                      className="w-full mt-1 px-3 py-2 border rounded-lg"
                    />
                  </div>
                ))}

                {/* Adj Weight — Round off (integer) */}
                <div>
                  <label className="block font-medium text-gray-700">Round Off</label>
                  <input
                    type="number"
                    step="1"
                    name="adjWeight"
                    value={form.adjWeight}
                    onChange={handleChange}
                    className="w-full mt-1 px-3 py-2 border rounded-lg"
                  />
                </div>

                {/* Holiday Charges */}
                <div>
                  <label className="block font-medium text-gray-700">Holiday Charges</label>
                  <input
                    type="number"
                    step="0.01"
                    name="holidayCharges"
                    value={form.holidayCharges}
                    onChange={handleChange}
                    className="w-full mt-1 px-3 py-2 border rounded-lg"
                  />
                </div>

                {/* Special Charges */}
                <div>
                  <label className="block font-medium text-gray-700">Special Charges</label>
                  <input
                    type="number"
                    step="0.01"
                    name="specialCharges"
                    value={form.specialCharges}
                    onChange={handleChange}
                    className="w-full mt-1 px-3 py-2 border rounded-lg"
                  />
                </div>

                <div>
                  <label className="block font-medium">Status</label>
                  <select
                    name="status"
                    value={form.status}
                    onChange={e => setForm(prev => ({ ...prev, status: e.target.value === 'true' }))}
                    className="w-full mt-1 px-3 py-2 border rounded-lg"
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-4 mt-8">
                <button
                  type="submit"
                  disabled={loading}
                  className="px-8 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-60"
                >
                  {loading ? 'Saving...' : editingId ? 'Update Rate' : 'Create Rate'}
                </button>
                <button
                  type="button"
                  onClick={() => { resetForm(); setShowList(true); }}
                  className="px-8 py-3 bg-gray-600 text-white font-semibold rounded-lg hover:bg-gray-700"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {/* LIST VIEW */}
          {showList && (
            <div>
              {loading ? (
                <div className="text-center py-12 text-gray-500">Loading records...</div>
              ) : paginated.length === 0 ? (
                <div className="text-center py-12 text-gray-500 text-lg">No records found</div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full table-auto border-collapse">
                      <thead className="bg-gradient-to-r from-blue-600 to-blue-800 text-white">
                        <tr>
                          <th className="p-4 text-left">S.No</th>
                          <th className="p-4 text-left">Customer</th>
                          <th className="p-4 text-left">From State</th>
                          <th className="p-4 text-left">To State</th>
                          <th className="p-4 text-left">Rate/Kg</th>
                          <th className="p-4 text-left">Status</th>
                          <th className="p-4 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginated.map((r, i) => {
                          const cust = customers.find(c => c._id === (r.customerId?._id || r.customerId));
                          const from = states.find(s => s._id === (r.fromStateId?._id || r.fromStateId));
                          const to = states.find(s => s._id === (r.toStateId?._id || r.toStateId));

                          // FIX: Index lookup for search consistency
                          const serialNumber = filter
                            ? records.findIndex(item => item._id === r._id) + 1
                            : (currentPage - 1) * pageSize + i + 1;

                          return (
                            <tr key={r._id} className="border-b hover:bg-gray-50">
                              <td className="p-4 text-gray-600 font-medium">{serialNumber}</td>
                              <td className="p-4">{cust?.name || 'N/A'}</td>
                              <td className="p-4">{from?.statename || 'N/A'}</td>
                              <td className="p-4">{to?.statename || 'N/A'}</td>
                              <td className="p-4 font-semibold text-green-600">₹{r.ratePerKg}</td>
                              <td className="p-4">
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${r.status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{r.status ? 'Active' : 'Inactive'}</span>
                              </td>
                              <td className="p-4 text-center">
                                <button onClick={() => handleEdit(r)} className="text-blue-600 hover:text-blue-800 mr-4"><MdEdit size={20} /></button>
                                <button onClick={() => handleDelete(r._id)} className="text-red-600 hover:text-red-800"><MdDelete size={20} /></button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {totalPages > 1 && (
                    <div className="flex justify-between items-center mt-8">
                      <span className="text-gray-600">Page {currentPage} of {totalPages}</span>
                      <div className="flex gap-2">
                        <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50">Previous</button>
                        <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50">Next</button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerPriceSetting;